/* eslint-disable react/no-array-index-key */
import * as pagination from '@zag-js/pagination';
import { normalizeProps, useMachine } from '@zag-js/react';
import { useId } from 'react';

import Button from 'molecules/Button';

import type { FC } from 'react';

interface PaginationProps {
  count: number;
  pageSize: number;
  setPage: (details: pagination.PageChangeDetails) => void;
  currentPage: number;
}

const Pagination: FC<PaginationProps> = ({ count, pageSize, setPage, currentPage }) => {
  // In 1.x the machine takes its props directly, so the duplicated `context`
  // block the old API needed is gone; count/pageSize/page live in one place.
  const service = useMachine(pagination.machine, {
    id: useId(),
    count,
    pageSize,
    page: currentPage,
    siblingCount: 0,
    onPageChange(results) {
      setPage(results);
    },
  });

  const api = pagination.connect(service, normalizeProps);
  const { page: activePage } = api;

  return (
    <>
      {api.totalPages > 1 && (
        <nav {...api.getRootProps()} aria-label="Pagination navigation">
          <ul className="flex items-center justify-around gap-3">
            <li>
              <Button
                startIcon="arrow-left"
                label="Back"
                hierarchy="linkGray"
                disabled={activePage === 1}
                {...api.getPrevTriggerProps()}
              />
            </li>
            <li className="flex items-center gap-3">
              <ul className="flex items-center gap-3">
                {api.pages.map((page, i) => {
                  if (page.type === 'page') {
                    return (
                      <li key={page.value}>
                        <button
                          type="button"
                          {...api.getItemProps(page)}
                          className="focus-outline-primary"
                          aria-label={`Page ${page.value}`}
                        >
                          <span
                            data-active={activePage === page.value}
                            className="flex min-w-10 items-center justify-center rounded-lg bg-transparent p-2 font-spaceGrotesk font-semibold text-gray-900 text-overline-md hover:bg-primary-button-hover data-[active=true]:bg-primary-button-default dark:text-white"
                          >
                            {page.value}
                          </span>
                        </button>
                      </li>
                    );
                  } else {
                    return (
                      <li key={`ellipsis-${i}`}>
                        <span {...api.getEllipsisProps({ index: i })}>&#8230;</span>
                      </li>
                    );
                  }
                })}
              </ul>
            </li>
            <li>
              <Button
                endIcon="arrow-right"
                label="Next"
                hierarchy="linkGray"
                disabled={api.totalPages === activePage}
                {...api.getNextTriggerProps()}
              />
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default Pagination;
